import { METHOD, RequestStatus } from 'shared/Requests';
import { useSubscriptionReferenceDate } from './BillingApi';
import { BlipApi } from 'api/BlipApi';
import { AiTokenUsageTotal } from 'models/billing/aiToken/AiTokenUsageTotal';
import { AiTokenDailyConsumption } from 'models/billing/aiToken/AiTokenDailyConsumption';
import { AiTokenBotConsumption } from 'models/billing/aiToken/AiTokenBotConsumption';

export default class BillingAiTokenConsumptionApi {
  private readonly BILLING_COMMAND_DESTINATION = 'postmaster@billing.blip.ai';
  private status = '';
  private useSubscriptionReferenceDate: boolean | undefined = undefined;

  public async getUseSubscriptionReferenceDate(): Promise<void> {
    if (this.useSubscriptionReferenceDate === undefined) {
      this.useSubscriptionReferenceDate = await useSubscriptionReferenceDate();
    }
  }

  public getStatus(): string {
    return this.status;
  }

  public setSuccess(): void {
    this.status = RequestStatus.SUCCESS;
  }

  public setErrorLog(e: unknown): void {
    const error = JSON.parse(<string>e);
    const {
      status,
    } = JSON.parse(error.message);
    this.status = status;
  }

  public async getTotalAiTokenConsumption(
    tenantId: string,
    month: string,
    storageType = 1,
  ): Promise<AiTokenUsageTotal> {
    await this.getUseSubscriptionReferenceDate();

    const uri =
      `/tenants/${tenantId}/monthly-message-metrics/total?month=${month}` +
      '&metricIdList=AIToken' +
      `&useSubscriptionReferenceDate=${this.useSubscriptionReferenceDate}` +
      `&storageType=${storageType}`;

    const command = {
      to: this.BILLING_COMMAND_DESTINATION,
      method: METHOD.GET,
      uri: uri,
    };

    try {
      const {
        response: { items },
      } = await BlipApi.sendCommand(command);
      this.setSuccess();
      return new AiTokenUsageTotal({ items });
    } catch (e) {
      this.setErrorLog(e);
      return new AiTokenUsageTotal({ items: [] });
    }
  }

  public async getDailyAiTokenConsumption(
    tenantId: string,
    month: string,
    storageType = 1,
  ): Promise<AiTokenDailyConsumption[]> {
    await this.getUseSubscriptionReferenceDate();

    const uri =
      `/tenants/${tenantId}/monthly-message-metrics/daily?month=${month}` +
      '&metricIdList=AIToken' +
      `&useSubscriptionReferenceDate=${this.useSubscriptionReferenceDate}` +
      `&storageType=${storageType}` +
      '&$take=-1';

    const command = {
      to: this.BILLING_COMMAND_DESTINATION,
      method: METHOD.GET,
      uri: uri,
    };

    try {
      const {
        response: { items },
      } = await BlipApi.sendCommand(command);
      this.setSuccess();
      return items.map((item: ConstructorParameters<typeof AiTokenDailyConsumption>[0]) => new AiTokenDailyConsumption(item));
    } catch (e) {
      this.setErrorLog(e);
      return [];
    }
  }

  public async getBotAiTokenConsumption(
    tenantId: string,
    month: string,
    storageType = 1,
  ): Promise<AiTokenBotConsumption[]> {
    await this.getUseSubscriptionReferenceDate();

    const uri =
      `/tenants/${tenantId}/monthly-message-metrics/monthly/by-bot?month=${month}` +
      '&metricIdList=AIToken' +
      `&useSubscriptionReferenceDate=${this.useSubscriptionReferenceDate}` +
      `&storageType=${storageType}` +
      '&$take=-1';

    const command = {
      to: this.BILLING_COMMAND_DESTINATION,
      method: METHOD.GET,
      uri: uri,
    };

    try {
      const {
        response: { items },
      } = await BlipApi.sendCommand(command);
      this.setSuccess();
      return items.map((item: ConstructorParameters<typeof AiTokenBotConsumption>[0]) => new AiTokenBotConsumption(item));
    } catch (e) {
      this.setErrorLog(e);
      return [];
    }
  }
}
